import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";
import { canUpload, duplicateMediaFromError, findDuplicate, hasSameNameAndMime, mergeMediaAssets, type MediaLibraryStatus } from "./media-library-state";
import type { ArticleCover, MediaAsset } from "./types";

type Props = { articleId?: string; cover: ArticleCover | null; onAssetsChange?: (assets: MediaAsset[]) => void; onCoverChange: (cover: ArticleCover | null) => void };

function duplicateDetails(asset: MediaAsset) {
  return <><b>Такой файл уже есть в медиатеке</b><span>{asset.originalName} · {asset.mimeType} · {(asset.byteSize / 1_048_576).toFixed(2)} МБ</span></>;
}

export function MediaPanel({ articleId, cover, onAssetsChange, onCoverChange }: Props) {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [status, setStatus] = useState<MediaLibraryStatus>("loading");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [duplicate, setDuplicate] = useState<MediaAsset | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadInFlight = useRef(false);
  const requestId = useRef(0);
  const onAssetsChangeRef = useRef(onAssetsChange);
  onAssetsChangeRef.current = onAssetsChange;

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setStatus("loading"); setError("");
    try {
      const items = await api.media(articleId);
      if (id !== requestId.current) return;
      setAssets(items); onAssetsChangeRef.current?.(items); setStatus("ready");
    } catch (reason) {
      if (id !== requestId.current) return;
      setError(reason instanceof Error ? reason.message : "Не удалось загрузить медиатеку"); setStatus("error");
    }
  }, [articleId]);

  useEffect(() => { void load(); return () => { requestId.current += 1; }; }, [load]);

  async function upload(file?: File) {
    if (!file || !canUpload(status) || uploadInFlight.current) return;
    uploadInFlight.current = true;
    setStatus("uploading"); setError(""); setWarning(""); setDuplicate(null);
    try {
      const known = await findDuplicate(file, assets);
      if (known) { setDuplicate(known); return; }
      if (assets.some((asset) => hasSameNameAndMime(file, asset))) {
        setWarning("Файл с таким именем и типом уже есть, но его содержимое отличается. Существующий файл не будет перезаписан.");
      }
      const asset = await api.uploadMedia(file, articleId);
      setAssets((current) => { const next = mergeMediaAssets(current, [asset]); onAssetsChangeRef.current?.(next); return next; });
    } catch (reason) {
      const existing = duplicateMediaFromError(reason);
      if (existing) {
        setDuplicate(existing);
        setAssets((current) => { const next = mergeMediaAssets(current, [existing]); onAssetsChangeRef.current?.(next); return next; });
      } else setError(reason instanceof Error ? reason.message : "Не удалось загрузить изображение");
    } finally {
      uploadInFlight.current = false; setStatus("ready");
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function chooseCover(asset: MediaAsset) {
    const alt = window.prompt("Альтернативный текст обложки", cover?.mediaId === asset.id ? cover.alt : "Обложка статьи");
    if (alt?.trim()) onCoverChange({ mediaId: asset.id, alt: alt.trim() });
  }
  const images = assets.filter((asset) => asset.kind === "image" && asset.mimeType !== "image/gif");
  const uploadEnabled = canUpload(status);
  return <section className="panel media-panel">
    <div className="panel-heading"><div><h2>Обложка</h2><p>Обложка статьи; медиа в текст вставляется через toolbar редактора.</p></div><button type="button" className="secondary-button compact" disabled={!uploadEnabled} onClick={() => fileRef.current?.click()}>{status === "uploading" ? "Загрузка…" : "+ Загрузить изображение"}</button></div>
    <p className="cover-hint">Рекомендуется статичная обложка 16:9, 1600 × 900 (минимум 1200 × 675), WebP/JPEG/PNG. Видео и GIF не используются для SEO/OG.</p>
    <input ref={fileRef} className="visually-hidden" disabled={!uploadEnabled} type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void upload(event.target.files?.[0])} />
    {status === "loading" ? <p className="media-empty">Загружаем изображения…</p> : null}
    {status === "error" ? <div className="notice error" role="alert">{error}<button type="button" className="quiet-button" onClick={() => void load()}>Повторить загрузку медиатеки</button></div> : null}
    {error && status !== "error" ? <div className="notice error" role="alert">{error}</div> : null}
    {warning ? <div className="notice warning" role="status">{warning}</div> : null}
    {duplicate ? <div className="notice warning" role="status"><div>{duplicateDetails(duplicate)}</div><button type="button" className="quiet-button" onClick={() => chooseCover(duplicate)}>Использовать существующий</button></div> : null}
    {status !== "loading" && status !== "error" ? images.length ? <div className="media-grid">{images.map((asset) => <article className={cover?.mediaId === asset.id ? "media-card selected" : "media-card"} key={asset.id}>
      <img src={"/api/media/" + asset.id + "/file"} alt="" loading="lazy" />
      <div><b title={asset.originalName}>{asset.originalName}</b><small>{asset.width} × {asset.height}{asset.width < 1200 || asset.height < 675 ? " · меньше рекомендуемого" : ""}{Math.abs(asset.width / asset.height - 16 / 9) > .08 ? " · не 16:9" : ""}</small></div>
      <div className="media-actions"><button type="button" onClick={() => chooseCover(asset)}>{cover?.mediaId === asset.id ? "Обложка выбрана" : "На обложку"}</button></div>
    </article>)}</div> : <p className="media-empty">Загрузите статичную обложку.</p> : null}
    {cover ? <div className="cover-setting"><label>Alt обложки<input value={cover.alt} onChange={(event) => onCoverChange({ ...cover, alt: event.target.value })} maxLength={300} /></label><button type="button" className="danger-link" onClick={() => onCoverChange(null)}>Убрать обложку</button></div> : null}
  </section>;
}
